/**
 * NotificationContentGiftItem
 *
 * contentGift — `notification_content_gift_item.dart:40-186`.
 * Title: `{name} sent you a gift`. Subtitle is two lines:
 *   1. `[{instances} x ] <gift-thumbnail> - {giftInfo.coins} <coin-icon>`
 *      (`giftInfo.coins` via `MathUtils.roundDoubleTwoDecimals`).
 *   2. `Total: {stats.coins} <coin-icon> · {date}`
 *      (`stats.coins` via `MathUtils.removeTrailingZero`).
 * Avatar from `usersInfo.userId`, trailing thumbnail from `mediaInfo`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { GiftUtils } from '@/domain/utils/giftUtils';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationAvatar } from './shared/NotificationAvatar';
import { NotificationMediaThumbnail } from './shared/NotificationMediaThumbnail';
import { NotificationCoinsDateLine } from './shared/NotificationCoinsDateLine';
import type { NotificationItemProps } from './notificationItemProps';

export const NotificationContentGiftItem: React.FC<NotificationItemProps> = ({
  notification,
  onUserTap,
  onItemTap,
  onItemMediaTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const n = l10n.inbox.notifications;

  const { info, stats } = notification;
  const mediaInfo = info.mediaInfo;
  const giftInfo = info.giftInfo;
  const name = info.usersInfo.name;

  const title = n.sentYouAGift.replace('{name}', name);

  const instances = giftInfo?.instances ?? 0;

  const subtitle = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: responsive.gap.xs }}>
      {/* Line 1: [instances x] gift thumbnail - coins */}
      {giftInfo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.gap.xs,
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
          }}
        >
          {instances > 1 && <span>{instances} x</span>}
          <CachedImage
            src={GiftUtils.getGiftPath(giftInfo.thumbnail)}
            alt={giftInfo.name}
            style={{
              width: responsive.icon.small,
              height: responsive.icon.small,
              objectFit: 'cover',
            }}
          />
          <span>- {MathUtils.roundDoubleTwoDecimals(giftInfo.coins)}</span>
          <AssetIcon
            path="/assets/images/coins.png"
            width={responsive.icon.xs}
            height={responsive.icon.xs}
            color={theme.exclusive}
          />
        </div>
      )}
      {/* Line 2: Total: stats.coins coin-icon · date */}
      <NotificationCoinsDateLine
        prefix={n.totalPrefix}
        amount={MathUtils.removeTrailingZero(stats?.coins ?? 0)}
        date={formatDate(info.timestamp)}
      />
    </div>
  );

  return (
    <NotificationRowLayout
      leading={<NotificationAvatar userId={info.usersInfo.userId} alt={name} />}
      title={title}
      subtitle={subtitle}
      trailing={mediaInfo ? <NotificationMediaThumbnail mediaInfo={mediaInfo} /> : undefined}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onLeadingTap={onUserTap ? () => onUserTap(info.usersInfo.userId) : undefined}
      onTrailingTap={onItemMediaTap ? () => onItemMediaTap(notification) : undefined}
    />
  );
};
